import type en from "./en";

const de: typeof en = {
  footer: {
    poweredBy: "Erstellt mit",
    help: "Hilfe",
  },

  tour: {
    next: "Weiter",
    skip: "Tour überspringen",
    done: "Verstanden!",
    stepCounter: "{current} von {total}",
    defaults: {
      canvas: {
        title: "Ihre E-Mail-Leinwand",
        text: "Hier nimmt Ihre E-Mail Gestalt an. Klicken Sie auf einen Block, um ihn auszuwählen und zu bearbeiten.",
      },
      sidebar: {
        title: "Block-Bibliothek",
        text: "Ziehen Sie Blöcke von hier auf die Leinwand, um Text, Bilder, Buttons und mehr hinzuzufügen.",
      },
      rightSidebar: {
        title: "Inhalt & Einstellungen",
        text: "Wählen Sie einen Block aus, um seinen Inhalt hier zu bearbeiten, oder wechseln Sie zu Einstellungen, um das gesamte Vorlagenlayout anzupassen.",
      },
    },
  },

  // History (undo/redo)
  history: {
    undo: "Rückgängig",
    redo: "Wiederholen",
    collabWarning:
      "Rückgängig machen kann die Änderungen anderer Mitarbeiter beeinflussen",
  },

  // Viewport toggle
  viewport: {
    label: "Ansichtsgröße",
    desktop: "Desktop",
    tablet: "Tablet",
    mobile: "Mobil",
  },

  // Dark mode preview
  darkMode: {
    enable: "Dunkelmodus-Vorschau",
    disable: "Hellmodus-Vorschau",
  },

  // Preview mode
  previewMode: {
    enable: "Vorschaumodus",
    disable: "Vorschau beenden",
  },

  // Sidebar - Block types
  blocks: {
    section: "Abschnitt",
    image: "Bild",
    title: "Titel",
    paragraph: "Absatz",
    button: "Schaltfläche",
    input: "Eingabefeld",
    divider: "Trennlinie",
    video: "Video",
    social: "Sozial",
    spacer: "Abstand",
    html: "HTML",
    menu: "Menü",
    table: "Tabelle",
    countdown: "Countdown",
    form: "Formular",
    discountCoupon: "Rabattcode",
  },

  // Right sidebar
  sidebar: {
    content: "Inhalt",
    settings: "Einstellungen",
    noSelection: "Kein Element ausgewählt",
    noSelectionHint:
      "Wählen Sie einen Block auf der Leinwand aus, um ihn zu bearbeiten",
  },

  // Toolbar - Common
  toolbar: {
    duplicate: "Duplizieren",
    delete: "Löschen",
  },

  // Title editor toolbar
  titleEditor: {
    toolbar: "Titelformatierung",
    bold: "Fett (Strg+B)",
    italic: "Kursiv (Strg+I)",
    addLink: "Link hinzufügen",
  },

  // Paragraph editor toolbar
  paragraphEditor: {
    toolbar: "Textformatierung",
    bold: "Fett (Strg+B)",
    italic: "Kursiv (Strg+I)",
    underline: "Unterstrichen (Strg+U)",
    strikethrough: "Durchgestrichen",
    subscript: "Tiefgestellt",
    superscript: "Hochgestellt",
    addLink: "Link hinzufügen",
    bulletList: "Aufzählungsliste",
    numberedList: "Nummerierte Liste",
    alignLeft: "Linksbündig",
    alignCenter: "Zentriert",
    alignRight: "Rechtsbündig",
    clearFormatting: "Formatierung entfernen",
    insertEmoji: "Emoji einfügen",
    fontFamily: "Schriftart",
    defaultFont: "Standard",
    fontSize: "Schriftgröße",
    defaultSize: "Standard",
    textColor: "Textfarbe",
    highlightColor: "Hervorhebungsfarbe",
    lineHeight: "Zeilenhöhe",
    letterSpacing: "Zeichenabstand",
    emojiItemLabel: "Emoji {emoji} einfügen",
    closeEmojiPicker: "Emoji-Auswahl schließen",
  },

  // Block actions (BlockWrapper)
  blockActions: {
    drag: "Zum Sortieren ziehen oder Leertaste drücken, um mit der Tastatur zu verschieben",
    dragLifted:
      "{block} wird verschoben. Pfeiltasten zum Positionieren, Leer- oder Eingabetaste zum Ablegen, Esc zum Abbrechen.",
    duplicate: "Block duplizieren",
    delete: "Block löschen",
    hiddenOnViewport: "Ausgeblendet auf {viewport}",
    saveAsModule: "Als Modul speichern",
    conditionToggle: "Anzeigebedingung umschalten",
    comments: "Kommentare ({count})",
    lifted: "{block} angehoben. Position {position} von {total}.",
    moved: "{block} auf Position {position} von {total} verschoben.",
    dropped: "{block} auf Position {position} von {total} abgelegt.",
    cancelled:
      "Verschieben abgebrochen. {block} auf Position {position} zurückgesetzt.",
  },

  // Toolbar - Section
  section: {
    dropHere: "Blöcke hierher ziehen",
    columns: "Spalten",
    column1: "1 Spalte",
    column2: "2 Spalten",
    column3: "3 Spalten",
    ratio12: "1:2 Verhältnis",
    ratio21: "2:1 Verhältnis",
  },

  // Text editor link dialog
  linkDialog: {
    editLink: "Link bearbeiten",
    insertLink: "Link einfügen",
    updateLink: "Link aktualisieren",
    removeLink: "Link entfernen",
    cancel: "Abbrechen",
    urlPlaceholder: "https://beispiel.de",
    urlLabel: "URL",
  },

  // Toolbar - Title
  title: {
    level: "Überschriftenebene",
    heading1: "Überschrift 1 (36px)",
    heading2: "Überschrift 2 (28px)",
    heading3: "Überschrift 3 (22px)",
    heading4: "Überschrift 4 (18px)",
    fontFamily: "Schriftart",
    inheritFont: "Vorlagenschrift verwenden",
    color: "Farbe",
    align: "Ausrichtung",
    alignLeft: "Links",
    alignCenter: "Zentriert",
    alignRight: "Rechts",
  },

  // Emoji picker
  emoji: {
    smileys: "Smileys",
    gestures: "Gesten",
    objects: "Objekte",
  },

  // Toolbar - Image
  image: {
    imageUrl: "Bild-URL",
    imageUrlPlaceholder: "https://...",
    altText: "Alternativtext",
    altTextPlaceholder: "Bildbeschreibung",
    width: "Breite",
    fullWidth: "Volle Breite",
    linkUrl: "Link-URL",
    openInNewTab: "In neuem Tab öffnen",
    placeholderUrl: "Platzhalterbild",
    placeholderUrlPlaceholder: "https://... (nur zur Gestaltung)",
    placeholderUrlTooltip:
      "Da die Bild-URL ein Merge-Tag verwendet, können Sie hier ein echtes Bild angeben, um das Layout während der Gestaltung in der Vorschau anzuzeigen. Dies wird nicht in die endgültige Ausgabe aufgenommen.",
    clickToAdd: "Klicken Sie, um eine Bild-URL hinzuzufügen",
    browseMedia: "Medien durchsuchen",
    decorative: "Dekoratives Bild",
    decorativeHint:
      "Wird von Bildschirmlesern ignoriert. Nur für Abstandshalter und visuelle Verzierungen verwenden.",
  },

  // Toolbar - Video
  video: {
    videoUrl: "Video-URL",
    videoUrlPlaceholder: "https://youtube.com/...",
    youtube: "YouTube",
    vimeo: "Vimeo",
    detected: "Video erkannt — Vorschaubild wird automatisch generiert",
    openInNewTab: "In neuem Tab öffnen",
    customThumbnail: "Eigenes Vorschaubild",
    optional: "(optional)",
    thumbnailPlaceholder: "Automatisch aus Video-URL generiert",
    altText: "Alternativtext",
    altTextPlaceholder: "Videobeschreibung",
    width: "Breite",
    fullWidth: "Volle Breite",
    placeholderUrl: "Platzhalter-Thumbnail",
    placeholderUrlPlaceholder: "https://... (nur zur Gestaltung)",
    placeholderUrlTooltip:
      "Da die Video-URL ein Merge-Tag verwendet, können Sie hier ein echtes Thumbnail angeben, um das Layout während der Gestaltung in der Vorschau anzuzeigen. Dies wird nicht in die endgültige Ausgabe aufgenommen.",
    addVideo: "Video-URL hinzufügen",
  },

  // Toolbar - Button
  button: {
    fontFamily: "Schriftart",
    inheritFont: "Vorlagenschrift verwenden",
    text: "Text",
    actionOnClick: "Aktion beim Klick",
    clickActionNone: "Aktion wählen",
    clickActionNextStep: "Nächsten Schritt anzeigen",
    clickActionPreviousStep: "Vorherigen Schritt anzeigen",
    clickActionLoadPage: "Neue Seite laden",
    clickActionScrollAnchor: "Zum Anker scrollen",
    clickActionClosePopup: "Popup schließen",
    url: "URL",
    pageUrl: "Seiten-URL",
    anchorId: "Anker-ID",
    anchorIdPlaceholder: "abschnitt-id",
    urlPlaceholder: "https://...",
    openInNewTab: "In neuem Tab öffnen",
    background: "Hintergrund",
    textColor: "Textfarbe",
    border: "Rahmen",
    borderColor: "Rahmenfarbe",
    borderWidth: "Rahmenbreite",
    borderSides: "Seiten",
    borderSidesLabels: {
      all: "Alle Seiten",
      top: "Oben",
      right: "Rechts",
      bottom: "Unten",
      left: "Links",
    },
    borderRadius: "Eckenradius",
    fontSize: "Schriftgröße",
  },

  // Toolbar - Input field
  input: {
    fieldType: "Eingabetyp",
    types: {
      text: "Text",
      email: "E-Mail",
      date: "Datum",
    },
    name: "Feldname",
    label: "Beschriftung",
    placeholder: "Platzhalter",
    defaultValue: "Standardwert",
    required: "Pflichtfeld",
    labelAppearance: "Beschriftung – Darstellung",
    labelMargin: "Beschriftung – Außenabstand",
    labelPadding: "Beschriftung – Innenabstand",
    fontFamily: "Schriftart",
    inheritFont: "Vorlagenschrift verwenden",
    fontWeight: "Schriftstärke",
    weightNormal: "Normal",
    weightBold: "Fett",
    alignLeft: "Links",
    alignCenter: "Zentriert",
    alignRight: "Rechts",
    inputAppearance: "Eingabefeld – Darstellung",
    inputMargin: "Eingabe – Außenabstand",
    inputPadding: "Eingabe – Innenabstand",
    fieldWidth: "Breite",
    fixedWidth: "Feste Breite",
    placeholderColor: "Platzhalterfarbe",
  },

  // Toolbar - Form (Mehrfeld-Anmeldung)
  form: {
    tabs: {
      sync: "Sync",
      fields: "Felder",
      button: "Button",
      terms: "Bedingungen",
    },
    sync: {
      heading: "Wähle deine Marketing-Plattform",
    },
    fieldTypes: {
      firstName: "Vorname",
      lastName: "Nachname",
      email: "E-Mail",
      phone: "Telefon",
    },
    fields: {
      addField: "Feld hinzufügen",
      noMoreFields: "Alle Feldtypen hinzugefügt",
      cancel: "Abbrechen",
      remove: "Feld entfernen",
      label: "Beschriftung",
      labelPlaceholder: "Wie ist deine E-Mail?",
      optional: "optional",
      placeholder: "Platzhalter",
      name: "Feldname",
      required: "Feld ist erforderlich",
      blockExisting: "Bekannte E-Mails sperren",
      blockExistingHint:
        "Verhindert die Anmeldung mit bereits gesammelten E-Mail-Adressen.",
      blockExistingAll: "Über alle Kampagnen prüfen",
      blockExistingThis: "Nur diese Kampagne prüfen",
      errorMessage: "Fehlermeldung",
    },
    button: {
      submitText: "Absende-Button",
      actionOnClick: "Aktion beim Klick",
      url: "Seiten-URL",
      position: "Anordnung",
      oneLine: "Eine Zeile",
      twoLines: "Zwei Zeilen",
      background: "Hintergrund",
      textColor: "Textfarbe",
      borderRadius: "Eckenradius",
    },
    buttonActions: {
      nextStep: "Nächsten Schritt anzeigen",
      closePopup: "Popup schließen",
      loadPage: "Neue Seite laden",
      none: "Nichts tun",
    },
    terms: {
      enable: "Bedingungen anzeigen",
      content: "Inhalt der Bedingungen",
      htmlHint:
        "HTML ist erlaubt. Nutze Links und Kleingedrucktes nach Bedarf.",
    },
  },

  discountCoupon: {
    description: "Gewinnen Sie Kunden mit einem Rabattcode für das Abonnement.",
    modeNone: "Kein Rabatt",
    modeAuto: "Rabattcode",
    modeManual: "Shopify-Rabatt manuell eingeben",
    autoHint:
      "Für jedes Abonnement einen einmaligen, nicht wiederverwendbaren Code erzeugen.",
    dynamicModesRequirePriorFormHint:
      "Der dynamische Code ist erst nach dem Opt-in-Schritt verfügbar.",
    selectType: "Typ wählen",
    types: {
      percentageOff: "Prozent rabatt",
      fixedAmount: "Fester Betrag",
      freeShipping: "Kostenloser Versand",
    },
    value: "Wert",
    currencyCode: "Währung",
    expiration: "Ablauf des Rabatts festlegen",
    expirationDaysSuffix: "Tage",
    tagCustomer: "Gesendeten Rabattcode als Tag beim Kunden hinzufügen",
    combinations: "Kombinationen",
    combineProduct: "Produktrabatte",
    combineOrder: "Bestellrabatte",
    combineShipping: "Versandrabatte",
    manualPlaceholder: "Rabattcode",
    manualFooterLead: "Rabatt erstellen",
    manualFooterTail: " in deinem Shopify-Admin und gib ihn oben ein.",
    manualBlankError: "Dieses Feld darf nicht leer sein",
    confirmationTextLabel: "Bestätigungstext",
    afterClickLabel: "Nach dem Klick",
    afterClickNone: "Nichts tun",
    afterClickLoadPage: "Zu URL wechseln",
    afterClickCloseCampaign: "Kampagne schließen",
    afterClickNextStep: "Nächsten Schritt anzeigen",
    afterClickPageUrl: "Seiten-URL",
    afterClickUrlPlaceholder: "https://...",
    previewNoDiscount: "Kein Rabatt",
    previewPercentage: "{value} % Rabatt",
    previewFixed: "{value} {currency} Rabatt",
    previewAutoUnique: "Einmaliger Code pro Abonnement",
    previewManualPrefix: "Code:",
    previewManualUnset: "Shopify-Rabattcode eingeben",
    previewExpires: "Läuft nach {days} Tag(en) ab",
    previewTag: "Kunde mit Code taggen",
    previewCombinations:
      "Kombinierbar mit Produkt- / Bestell- / Versandrabatten",
    previewSampleCode: "THANKYOU20",
    copyCode: "Code kopieren",
    copyCodeFlash: "Kopiert",
  },

  // Toolbar - Divider
  divider: {
    style: "Stil",
    solid: "Durchgehend",
    dashed: "Gestrichelt",
    dotted: "Gepunktet",
    color: "Farbe",
    thickness: "Stärke",
  },

  // Toolbar - Social Icons
  social: {
    icons: "Symbole",
    addIcon: "Symbol hinzufügen",
    addIcons: "Soziale Symbole hinzufügen",
    removeIcon: "Entfernen",
    platform: "Plattform",
    url: "URL",
    urlPlaceholder: "https://...",
    style: "Stil",
    styleSolid: "Gefüllt",
    styleOutlined: "Umrandet",
    styleRounded: "Abgerundet",
    styleSquare: "Eckig",
    styleCircle: "Rund",
    size: "Größe",
    sizeSmall: "K",
    sizeMedium: "M",
    sizeLarge: "G",
    spacing: "Abstand",
    align: "Ausrichtung",
    platforms: {
      facebook: "Facebook",
      twitter: "X (Twitter)",
      instagram: "Instagram",
      linkedin: "LinkedIn",
      youtube: "YouTube",
      tiktok: "TikTok",
      pinterest: "Pinterest",
      email: "E-Mail",
      whatsapp: "WhatsApp",
      telegram: "Telegram",
      discord: "Discord",
      snapchat: "Snapchat",
      reddit: "Reddit",
      github: "GitHub",
      dribbble: "Dribbble",
      behance: "Behance",
    },
  },

  // Toolbar - Menu
  menu: {
    items: "Menüpunkte",
    addItem: "Punkt hinzufügen",
    removeItem: "Entfernen",
    text: "Text",
    url: "URL",
    urlPlaceholder: "https://beispiel.de",
    openInNewTab: "In neuem Tab öffnen",
    bold: "Fett",
    underline: "Unterstrichen",
    color: "Farbe",
    linkColor: "Linkfarbe",
    fontSize: "Schriftgröße",
    fontFamily: "Schriftfamilie",
    separator: "Trennzeichen",
    separatorColor: "Trennzeichenfarbe",
    spacing: "Abstand",
    textAlign: "Ausrichtung",
    addLinks: "Menülinks hinzufügen",
  },

  // Toolbar - Table
  table: {
    dimensions: "Abmessungen",
    rows: "Zeilen",
    columns: "Spalten",
    addRow: "Zeile hinzufügen",
    removeRow: "Zeile entfernen",
    addColumn: "Spalte hinzufügen",
    removeColumn: "Spalte entfernen",
    hasHeaderRow: "Kopfzeile",
    headerBackgroundColor: "Kopfzeilen-Hintergrund",
    noHeaderBg: "Kein Hintergrund",
    borderColor: "Rahmenfarbe",
    borderWidth: "Rahmenbreite",
    cellPadding: "Zellenabstand",
    fontFamily: "Schriftart",
    fontSize: "Schriftgröße",
    color: "Textfarbe",
    textAlign: "Ausrichtung",
    cellPlaceholder: "Text eingeben...",
    empty: "Tabelle hinzufügen",
  },

  // Toolbar - Spacer
  spacer: {
    height: "Höhe",
  },

  // Toolbar - Countdown
  countdown: {
    targetDate: "Zieldatum",
    timezone: "Zeitzone",
    display: "Anzeige",
    days: "Tage",
    hours: "Stunden",
    minutes: "Minuten",
    seconds: "Sekunden",
    separator: "Trennzeichen",
    fontFamily: "Schriftart",
    inheritFont: "Standard",
    digitFontSize: "Zifferngröße",
    digitColor: "Ziffernfarbe",
    labelColor: "Beschriftungsfarbe",
    labelFontSize: "Beschriftungsgröße",
    background: "Hintergrund",
    labels: "Beschriftungen",
    expiry: "Ablaufnachricht",
    expiredMessagePlaceholder: "Dieses Angebot ist abgelaufen",
    expiredImageUrl: "Ablaufbild-URL",
    hideOnExpiry: "Bei Ablauf ausblenden",
    setDate: "Legen Sie ein Zieldatum im Einstellungsbereich fest",
    hidden: "Ausgeblendet (abgelaufen)",
    style: "Stil",
    layout: "Layout",
    animationSpeed: "Animationsgeschwindigkeit",
    accentColor: "Akzentfarbe",
    cardGap: "Kartenabstand",
    cardRadius: "Eckenradius",
    enableHoverFx: "Hover-Effekte aktivieren",
  },

  // Custom Blocks
  customBlocks: {
    definitionNotFound:
      "Unbekannter Blocktyp — dieser Block ist nicht registriert",
    renderError:
      "Dieser Block konnte nicht gerendert werden. Überprüfen Sie die Block-Vorlage auf Fehler.",
    fields: {
      required: "Pflichtfeld",
      addItem: "Element hinzufügen",
      removeItem: "Entfernen",
      maxItemsReached: "Maximale Anzahl erreicht",
      minItemsRequired: "Mindestens {count} Elemente erforderlich",
    },
    toolbar: {
      noDefinition:
        "Registrieren Sie diesen Blocktyp in Ihrer SDK-Konfiguration, um seine Eigenschaften zu bearbeiten",
    },
    dataSource: {
      fetchButton: "Inhalt laden",
      changeButton: "Ändern",
      fetching: "Wird geladen...",
      readOnlyTooltip: "Dieser Wert wird aus Ihrer Datenquelle geladen",
      fetchError: "Inhalt konnte nicht geladen werden",
    },
  },

  // Toolbar - HTML
  html: {
    content: "HTML-Inhalt",
    preview: "Benutzerdefinierter HTML-Block",
    empty: "HTML-Inhalt im Bereich hinzufügen",
    sanitizationHint:
      "Skripte und unsichere Elemente werden beim Export entfernt.",
  },

  // Toolbar - Common block settings
  blockSettings: {
    spacing: "Abstände",
    padding: "Innenabstand",
    margin: "Außenabstand",
    background: "Hintergrund",
    color: "Farbe",
    display: "Anzeige",
    showOnDesktop: "Auf Desktop anzeigen",
    showOnTablet: "Auf Tablet anzeigen",
    showOnMobile: "Auf Mobilgerät anzeigen",
    hiddenOnDevice: "Ausgeblendet auf {device}",
    customCss: "Benutzerdefiniertes CSS",
    css: "CSS",
    cssPlaceholder: "/* Benutzerdefinierte Stile */",
    displayCondition: "Anzeigebedingung",
    selectCondition: "Bedingung auswählen",
    removeCondition: "Bedingung entfernen",
    noCondition: "Immer sichtbar",
    conditionApplied: "Bedingung angewendet",
    customCondition: "Eigene Bedingung",
    customConditionLabel: "Bedingungsname",
    customConditionBefore: "Vorher (öffnende Logik)",
    customConditionAfter: "Nachher (schließende Logik)",
    applyCondition: "Anwenden",
    cancelCondition: "Abbrechen",
    customBadge: "Eigene",
    restoreHiddenBlocks: "Alle ausgeblendeten Blöcke anzeigen",
  },

  // Template settings
  templateSettings: {
    layout: "Layout",
    widthPreset: "Breitenvoreinstellung",
    customWidth: "Benutzerdefinierte Breite",
    contentPadding: "Innenabstand",
    appearance: "Erscheinungsbild",
    backgroundColor: "Hintergrundfarbe",
    backgroundOpacity: "Hintergrundtransparenz",
    backgroundShadow: "Hintergrundschatten",
    fontFamily: "Schriftfamilie",
    preheaderText: "Preheader-Text",
    preheaderTextPlaceholder:
      "Vorschautext, der nach der Betreffzeile im Posteingang angezeigt wird...",
    preheaderTextHint:
      "Dieser Text erscheint nach der Betreffzeile in der E-Mail-Vorschau. Unterstützt Merge-Tags.",
    language: "Sprache",
    contentLocale: "Inhaltssprache",
    contentLocaleHint:
      "BCP-47-Code (z. B. en, de, pt-BR). Setzt das lang-Attribut der gerenderten E-Mail, damit Screenreader den Inhalt korrekt aussprechen.",
    tips: "Tipps",
    tip1: "600px ist die Standardbreite für E-Mail-Vorlagen",
    tip2: "Verwenden Sie websichere Schriften für beste Kompatibilität",
    tip3: "Helle Hintergründe eignen sich am besten für die Lesbarkeit",
  },

  // Spacing control
  spacingControl: {
    lockAll: "Alle Seiten sperren",
    unlock: "Seiten entsperren",
    top: "Oben",
    right: "Rechts",
    bottom: "Unten",
    left: "Links",
    decreaseTop: "Oben verringern",
    increaseTop: "Oben erhöhen",
    decreaseLeft: "Links verringern",
    increaseLeft: "Links erhöhen",
    decreaseRight: "Rechts verringern",
    increaseRight: "Rechts erhöhen",
    decreaseBottom: "Unten verringern",
    increaseBottom: "Unten erhöhen",
  },

  // Color Picker
  colorPicker: {
    pickColor: "Farbe auswählen",
    hexValue: "Hex-Farbwert",
  },

  // Merge-Tag
  mergeTag: {
    clickToEdit: "Zum Bearbeiten klicken",
    remove: "Merge-Tag entfernen",
    insert: "Merge-Tag einfügen",
    add: "Merge-Tag hinzufügen",
    editValue: "Merge-Tag-Wert bearbeiten",
    deleteMergeTag: "Merge-Tag löschen",
    suggestionEmpty: "Keine passenden Merge-Tags",
  },

  // Canvas
  canvas: {
    noBlocks: "Noch keine Blöcke",
    dragHint:
      "Beginnen Sie von Grund auf, indem Sie Blöcke aus der Seitenleiste ziehen",
    dropHere: "Hier ablegen",
    aiHintChat: "oder lassen Sie",
    aiHintChatSuffix: "in Sekunden eine komplette Vorlage für Sie erstellen",
    aiHintDesign:
      "Haben Sie ein bestehendes Design? Laden Sie einen Screenshot, ein Bild oder PDF hoch und",
    aiHintDesignSuffix: "erstellt es sofort nach",
    pages: {
      ariaStrip: "Schritte",
      addStep: "Schritt hinzufügen",
      stepActions: "Schrittaktionen",
      rename: "Umbenennen",
      delete: "Löschen",
      renamePrompt: "Schrittname",
      unreachableLabel: "Nicht erreichbarer Schritt",
      unreachableTooltip:
        "Dieser Schritt ist nicht erreichbar. Auf dem vorherigen Schritt ist keine Schaltfläche und kein Formular mit „Nächsten Schritt anzeigen“ (oder gleichwertig) eingestellt, die bzw. das hierher führt.",
    },
  },

  // Media Library (cloud)
  mediaLibrary: {
    title: "Medienbibliothek",
    searchPlaceholder: "Dateien suchen...",
    allFiles: "Alle Dateien",
    filterAll: "Alle Typen",
    filterImages: "Bilder",
    filterDocuments: "Dokumente",
    filterVideos: "Videos",
    filterAudio: "Audio",
    newFolder: "Neuer Ordner",
    folderName: "Ordnername",
    noFiles: "Keine Dateien gefunden",
    dropOrClick: "Dateien hierher ziehen oder klicken zum Hochladen",
    acceptedFormats: "Bilder, PDF, Video, Audio, Dokumente (max. 10 MB)",
    uploading: "Wird hochgeladen...",
    uploadingProgress: "{current} von {total} wird hochgeladen...",
    selectImage: "Bild auswählen",
    selectFile: "Datei auswählen",
    deleteSelected: "Löschen",
    copyUrl: "URL kopieren",
    copied: "Kopiert!",
    browseMedia: "Medienbibliothek durchsuchen",
    renameFolder: "Ordner umbenennen",
    addSubfolder: "Unterordner hinzufügen",
    subfolderName: "Unterordnername",
    sortNewest: "Neueste zuerst",
    sortOldest: "Älteste zuerst",
    sortNameAsc: "Name A-Z",
    sortNameDesc: "Name Z-A",
    sortSizeAsc: "Kleinste zuerst",
    sortSizeDesc: "Größte zuerst",
    moveSelected: "Verschieben",
    moveToRoot: "Alle Dateien",
    currentFolder: "(aktuell)",
    confirmDelete: "Diese Datei löschen?",
    renameFile: "Umbenennen",
    editFile: "Datei bearbeiten",
    fileName: "Dateiname",
    altText: "Alternativtext",
    altTextPlaceholder: "Bildbeschreibung für Barrierefreiheit",
    saveChanges: "Speichern",
    cancel: "Abbrechen",
    frequentlyUsed: "Häufig verwendet",
    deleteWarningTitle: "Datei löschen",
    deleteWarningMessage:
      "Diese Datei wird dauerhaft gelöscht und kann nicht wiederhergestellt werden.",
    deleteWarningUsageNote:
      "Die folgenden Dateien werden in Vorlagen verwendet. Das Löschen kann diese Vorlagen beschädigen.",
    deleteAnyway: "Datei löschen",
    usedInTemplates: "In {count} Vorlage(n) verwendet",
    viewGrid: "Rasteransicht",
    viewList: "Listenansicht",
    showFolders: "Ordner anzeigen",
    hideFolders: "Ordner ausblenden",
    importFromUrl: "Von URL importieren",
    importUrlPlaceholder: "https://example.com/image.jpg",
    import: "Importieren",
    importing: "Wird importiert...",
    importError: "Import von URL fehlgeschlagen",
    conversionLabel: "Groesse",
    conversionOriginal: "Original",
    conversionSmall: "Klein (150px)",
    conversionMedium: "Mittel (600px)",
    conversionLarge: "Gross (1200px)",
    replaceFile: "Datei ersetzen",
    replaceWarningTitle: "Datei ersetzen",
    replaceWarningMessage:
      "Sie sind dabei, diese Datei zu ersetzen. Die Ersatzdatei muss dieselbe Dateierweiterung haben ({extension}).",
    replaceWarningUsageNote:
      "Diese Datei wird in {count} Vorlage(n) verwendet. Das Ersetzen aktualisiert alle Verweise.",
    replaceSelectFile: "Ersatzdatei auswählen",
    replace: "Ersetzen",
    replacing: "Wird ersetzt...",
    replaceError: "Ersetzen der Datei fehlgeschlagen",
    saving: "Wird gespeichert...",
    cropAspectRatio: "Seitenverhältnis",
    cropFree: "Frei",
    cropSquare: "1:1",
    cropLandscape43: "4:3",
    cropLandscape169: "16:9",
    cropOriginal: "Original",
    cropMaxWidth: "Max. Breite",
    cropMaxHeight: "Max. Höhe",
    cropOutputSize: "Ausgabegröße",
    cropPixels: "px",
    cropOptional: "(optional)",
    storageTooltip: "{used} von {total} verwendet ({remaining} verfügbar)",
  },

  // Seitenleiste & Popup-Leiste
  popupSidebar: {
    workspaceLabel: "Popup-Editorwerkzeuge",
    railNavLabel: "Editorbereiche",
    railDesign: "Design",
    railBlocks: "Blöcke",
    railDisplayRules: "Anzeigeregeln",
    railSchedule: "Zeitplan",
    placeholderDesign: "Design-Einstellungen erscheinen hier.",
    placeholderDisplayRules: "Anzeigeregeln erscheinen hier.",
    placeholderSchedule: "Planungsoptionen erscheinen hier.",
  },

  popupSchedule: {
    timeZone: "Zeitzone",
    startDate: "Startdatum",
    endDate: "Enddatum",
    startImmediate: "Sofort",
    endNone: "Keines",
    clearStartDate: "Startdatum löschen",
    clearEndDate: "Enddatum löschen",
    needHelp: "Ich brauche Hilfe",
    detailedSchedule: "Detaillierter Zeitplan",
    detailedScheduleHeading: "Detaillierter Zeitplan",
    removeSlot: "Zeitfenster entfernen",
    addTimeSlot: "+ Zeitfenster hinzufügen",
    calendarPrevMonth: "Voriger Monat",
    calendarNextMonth: "Nächster Monat",
    calendarToday: "Heute",
    calendarClear: "Datum löschen",
    calendarDialogLabel: "Datum wählen",
    dayLabels: {
      everyday: "Täglich",
      monday: "Montag",
      tuesday: "Dienstag",
      wednesday: "Mittwoch",
      thursday: "Donnerstag",
      friday: "Freitag",
      saturday: "Samstag",
      sunday: "Sonntag",
    },
  },

  popupDisplayRules: {
    debug: "Debug",
    tabTrigger: "Trigger",
    tabPages: "Seiten",
    tabAudience: "Zielgruppe",
    tabFrequency: "Häufigkeit",
    tabAdvanced: "Erweitert",
    placeholderSubtab: "Dieser Bereich wird später ergänzt.",
    triggerHeading: "Trigger",
    triggerIntro: "Legen Sie fest, wann Besucher diese Kampagne sehen.",
    triggerIntroSecondary:
      "Mehrere Trigger können aktiv sein — es gilt die erste erfüllte Bedingung.",
    learnMore: "Mehr erfahren",
    addTriggerConditions: "Trigger-Bedingungen hinzufügen",
    triggerShowOnlyAfter: "Erst anzeigen nach",
    triggerSecondsOnPage: "Sekunden auf der Seite",
    triggerPageViewsSuffix: "Seitenaufrufen",
    triggerAddRule: "Regel hinzufügen",
    triggerRemoveRuleAria: "Seitenaufruf-Regel entfernen",
    triggerOperatorGroupAria: "Verzögerung und Seitenaufrufe verknüpfen",
    operatorAndShort: "UND",
    operatorOrShort: "ODER",
    triggerDelaySeconds: "Verzögerung (Sekunden)",
    triggerPageViews: "Mindest-Seitenaufrufe",
    triggerOperator: "Regeln verknüpfen mit",
    operatorAnd: "Alle (UND)",
    operatorOr: "Eine (ODER)",
    triggerOnScrollUpLabel: "Beim Hochscrollen auslösen",
    triggerOnBackButtonLabel: "Beim Zurück-Button des Browsers",
    debugJsonTitle: "Trigger-JSON (Embed-Form)",
    debugJsonHint:
      "Kurze Schlüssel (landing, exit, scroll, …) für Runtime und Integrationen. Im Editor weiterhin onLanding, onExit, …",
    copyJson: "JSON kopieren",
    copyJsonDone: "Kopiert",
    debugJsonClose: "Schließen",
    scrollShowAfter: "Einblenden nach",
    scrollPercentSuffix: "% der Ansicht",
    clickHint:
      'Fragment an den Link anhängen, um das Popup zu öffnen. Beispiel: <a href="#popup"></a>',
    hoverPlaceholder: "ID eines Elements",
    help: "Hilfe",
    customEventLabel: "Ereignisname",
    customEventPlaceholder: "meinPopupOpen",
    triggerOnLandingTitle: "Beim Laden",
    triggerOnLandingDesc: "Wenn ein Besucher eine Seite lädt.",
    triggerOnExitTitle: "Beim Verlassen (Exit Intent)",
    triggerOnExitDesc:
      "Wenn ein Besucher die Seite verlassen will — Popup vor dem Weggang zeigen.",
    triggerOnScrollTitle: "Beim Scrollen",
    triggerOnScrollDesc: "Wenn ein Besucher nach unten scrollt.",
    triggerOnClickTitle: "Beim Klick",
    triggerOnClickDesc: "Wenn ein Besucher einen Link anklickt.",
    triggerOnHoverTitle: "Beim Hover",
    triggerOnHoverDesc: "Wenn ein Besucher über ein Element fährt.",
    triggerOnCustomTitle: "Eigenes Ereignis",
    triggerOnCustomDesc: "Einen eigenen Trigger verwenden.",
    frequencyHeading: "Häufigkeit",
    frequencyIntro: "Legen Sie fest, wie oft Besucher diese Kampagne sehen.",
    frequencyShowAgainTitle: "Erneut anzeigen",
    frequencyShowAgainDesc:
      "Legen Sie fest, wie oft Ihre Kampagne eingeblendet wird.",
    frequencyEveryPageLoad: "Bei jedem Seitenaufruf",
    frequencyEverySession: "Pro Sitzung",
    frequencyEveryPrefix: "Alle",
    frequencyIntervalUnitMinutes: "Minuten",
    frequencyIntervalUnitHours: "Stunden",
    frequencyIntervalUnitDays: "Tage",
    frequencyStopShowingTitle: "Nicht mehr anzeigen",
    frequencyStopShowingDesc:
      "Legen Sie fest, wann ein Besucher die Kampagne nicht mehr sieht.",
    frequencyStopAfterSignup:
      "Nachdem sich ein Besucher angemeldet oder geklickt hat",
    frequencyStopAfterSeen: "Nachdem ein Besucher die Kampagne gesehen hat",
    frequencyStopTimesSuffix: "Mal",
    frequencyStopAfterClosedPrefix:
      "Nachdem ein Besucher die Kampagne geschlossen hat,",
    frequencyClosedNeverAgain: "und nie wieder anzeigen",
    frequencyClosedRespectRules:
      "aber die Regeln für „Erneut anzeigen“ beachten",
    frequencyAfterClosedHelp:
      "Was passiert, wenn jemand das Popup ohne Anmeldung oder Klick schließt.",
  },

  popupDesign: {
    tabStyle: "Stil",
    tabPosition: "Position",
    tabClosing: "Schließen",
    tabCss: "CSS",
    placeholderSubtab:
      "CSS-Anpassungen werden in einem späteren Update ergänzt.",
    standardSize: "Standardgröße",
    shapeHeading: "Form des Panels",
    shapeLabels: {
      normal: "Normal",
      circle: "Kreis",
      semiCircle: "Halbkreis",
      triangle: "Dreieck",
      pill: "Pille",
      hexagon: "Hexagon",
      diamond: "Raute",
    },
    panelAppearance: "Darstellung des Panels",
    panelColorHint:
      "Die Volltonfüllung nutzt die Hintergrundfarbe der Vorlage unter Einstellungen.",
    panelBackgroundOpacity: "Deckkraft der Panel-Füllung",
    contentScrimOpacity: "Abdunklung über dem Inhalt",
    backgroundImageUrl: "URL des Hintergrundbilds",
    backgroundImageFitHeading: "Anpassung Hintergrundbild",
    fitAuto: "Automatisch (natürliches Seitenverhältnis)",
    fitCover: "Ausfüllen (Cover)",
    fitContain: "Einpassen (Contain)",
    backgroundImageOnly: "Nur Bild (Chrome des Inhalts ausblenden)",
    options: "Optionen",
    useOverlay: "Overlay verwenden",
    overlayBlur: "Hintergrund-Unschärfe",
    overlayBlurHint:
      "Verwischt den Bereich hinter dem Popup. Auf 0 ziehen für einen scharfen Hintergrund.",
    overlayImageOpacity: "Deckkraft des Overlay-Bilds",
    overlayImage: "Overlay-Bild",
    overlayImagePanelAriaLabel: "Overlay-Bild hochladen",
    overlayImageDropLineBefore: "Bild hierher ziehen oder ",
    overlayImageBrowse: "durchsuchen",
    overlayImageDropLineAfter: ".",
    overlayImageFormats: "JPEG, PNG, GIF, WebP, AVIF oder SVG.",
    overlayImageDesktopPreview: "Desktop-Vorschau Overlay",
    overlayImageMobilePreview: "Mobile Vorschau Overlay",
    overlayImageCrop: "Zuschneiden",
    overlayImageReplace: "Ersetzen",
    overlayImageRemove: "Entfernen",
    backgroundImage: "Hintergrundbild",
    multiColumn: "Mehrspaltiges Layout",
    rtl: "Rechts-nach-links aktivieren",
    positionHeading: "Wählen Sie, wo das Popup angezeigt wird",
    displayAnimationHeading: "Öffnungsanimation wählen",
    animationSmooth: "Sanft",
    animationFadeIn: "Einblenden",
    animationSlideIn: "Einschieben",
    animationNone: "Keine",
    positionLabels: {
      topLeft: "Oben links",
      topCenter: "Oben Mitte",
      topRight: "Oben rechts",
      middleLeft: "Mitte links",
      center: "Mitte",
      middleRight: "Mitte rechts",
      bottomLeft: "Unten links",
      bottomCenter: "Unten Mitte",
      bottomRight: "Unten rechts",
    },
    closingDisplayCloseButton: "Schließen-Schaltfläche anzeigen",
    closingButtonPosition: "Position der Schließen-Schaltfläche",
    closingButtonPositionLeft: "Links",
    closingButtonPositionRight: "Rechts",
    closingOnOverlayClick: "Schließen bei Klick auf Overlay",
    sizeLabels: {
      small: "Klein",
      medium: "Mittel",
      large: "Groß",
      fullscreen: "Vollbild",
      fullHeight: "Volle Höhe",
      custom: "Benutzerdefiniert",
    },
  },

  sidebarNav: {
    browseModules: "Gespeicherte Module durchsuchen",
    expandSidebar: "Block-Seitenleiste erweitern",
    palette: "Blockpalette",
    insertBlock: "{block}-Block einfügen",
    paletteDiscountCouponTaken:
      "Es ist nur ein Rabattcoupon für alle Schritte zulässig.",
    paletteFormTaken:
      "Es ist nur ein Formularblock für alle Schritte zulässig.",
  },

  // Landmark-Bezeichnungen für Hilfstechnologien
  landmarks: {
    canvas: "E-Mail-Leinwand",
    blockToolbar: "Blockeigenschaften",
    rightSidebar: "Blockeigenschaften und Vorlageneinstellungen",
    reorderAnnouncements: "Block-Neuanordnungsmeldungen",
  },

  // Design Reference (cloud)
  errors: {
    editorLoading: "Editor wird geladen...",
    editorLoadFailed: "Editor konnte nicht geladen werden.",
    retry: "Erneut versuchen",
  },

  accessibility: {
    panelTitle: "Barrierefreiheit",
    panelTabLabel: "Barrierefreiheit",
    groupErrors: "Fehler",
    groupWarnings: "Warnungen",
    groupInfo: "Hinweise",
    jump: "Zum Block springen",
    fix: "Beheben",
    emptyState: "Keine Probleme mit der Barrierefreiheit.",
    badgeError: "Hat Barrierefreiheitsfehler",
    badgeWarning: "Hat Barrierefreiheitswarnungen",
    issueCountTooltip: "{count} Barrierefreiheitsprobleme",
  },
};

export default de;
